import { css } from "@emotion/css";
import { space } from "@octopusdeploy/design-system-tokens";
import type { EnvironmentResource } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import ScopedProjectTemplateScopeEdit from "~/areas/projects/components/Variables/TenantVariables/ScopedTenantVariables/ScopedProjectTemplateScopeEdit";
import { type ScopedProjectTemplatesScopeChangeTracker, type ScopedProjectTemplatesScopeKey } from "~/areas/projects/components/Variables/TenantVariables/ScopedTenantVariables/useScopedProjectTemplatesScopeChangeTracker";
import { EnvironmentChip } from "~/components/Chips/index";
import type { PopoverPosition } from "~/components/PopoverWhenFocused/PopoverWhenFocused";
import PopoverWhenFocused from "~/components/PopoverWhenFocused/index";
import ChangedValueIndicator from "../ChangedValueIndicator";
interface ScopedProjectTemplateScopeInlineCellProps {
    environments: EnvironmentResource[];
    scopeTracker: ScopedProjectTemplatesScopeChangeTracker;
    scopeKey: ScopedProjectTemplatesScopeKey;
    focused: boolean;
    onFocus: () => void;
    onBlur: () => void;
    hasEditPermissions: boolean;
    hasScopeChanged: boolean;
    initialEnvironmentScope: string[];
}
export default function ScopedProjectTemplateScopeInlineCell({ environments, scopeTracker, scopeKey, focused, onFocus, onBlur, hasEditPermissions, hasScopeChanged, initialEnvironmentScope }: ScopedProjectTemplateScopeInlineCellProps) {
    return (<ScopedProjectTemplateScopeEditableCell environments={environments} scopeTracker={scopeTracker} scopeKey={scopeKey} focused={focused} onFocus={onFocus} onBlur={onBlur} hasEditPermissions={hasEditPermissions} hasScopeChanged={hasScopeChanged} initialEnvironmentScope={initialEnvironmentScope}/>);
}
interface ScopedProjectTemplateScopeEditableCellProps extends ScopedProjectTemplateScopeInlineCellProps {
    hasEditPermissions: boolean;
}
function ScopedProjectTemplateScopeEditableCell({ environments, scopeTracker, scopeKey, focused, onFocus, onBlur, hasEditPermissions, hasScopeChanged, initialEnvironmentScope }: ScopedProjectTemplateScopeEditableCellProps) {
    const calculateFocusedPopoverPosition = (): PopoverPosition => {
        return {
            top: 0,
            left: 0,
            right: 0,
        };
    };
    return (<div onFocus={onFocus} onClick={onFocus} className={cellStyles.container}>
            <PopoverWhenFocused isFocused={focused} onClickOutside={onBlur} dynamicPosition={calculateFocusedPopoverPosition}>
                {focused ? (<ScopedProjectTemplateScopeEdit environments={environments} scopeChangeTracker={scopeTracker} scopeKey={scopeKey} initialEnvironmentScope={initialEnvironmentScope}/>) : (<div className={cellStyles.readonlyContainer}>
                        <ScopedProjectTemplateScopeReadonlyCell environments={environments} scopeKey={scopeKey} scopeChangeTracker={scopeTracker} initialEnvironmentScope={initialEnvironmentScope} hasEditPermissions={hasEditPermissions} hasScopeChanged={hasScopeChanged}/>
                        <div className={cellStyles.changedValueIndicator}>
                            <ChangedValueIndicator show={hasScopeChanged}/>
                        </div>
                    </div>)}
            </PopoverWhenFocused>
        </div>);
}
interface ScopedProjectTemplateScopeReadonlyCellProps {
    environments: EnvironmentResource[];
    scopeKey: ScopedProjectTemplatesScopeKey;
    scopeChangeTracker: ScopedProjectTemplatesScopeChangeTracker;
    initialEnvironmentScope: string[];
    hasEditPermissions: boolean;
    hasScopeChanged: boolean;
}
const ScopedProjectTemplateScopeReadonlyCell = (props: ScopedProjectTemplateScopeReadonlyCellProps) => {
    const { environments, scopeKey, scopeChangeTracker, initialEnvironmentScope } = props;
    const environmentMap = new Map<string, EnvironmentResource>();
    environments.forEach((e) => environmentMap.set(e.Id, e));
    const scope = scopeChangeTracker.has(scopeKey) ? scopeChangeTracker.get(scopeKey) : initialEnvironmentScope;
    const scopeLength = scope?.length ?? 0;
    const tooManyChips = scopeLength >= 3;
    if (!scope?.length) {
        return null;
    }
    return (<div tabIndex={0}>
            {tooManyChips ? (<EnvironmentChip environmentName={`${scopeLength} environments`} key="environmentsChipsList" description={scope.map((s) => environmentMap.get(s)?.Name).join(", ")}/>) : (<>
                    {scope.map((s) => {
                const environment = environmentMap.get(s);
                return environment ? <EnvironmentChip environmentName={environment.Name} key={environment.Id}/> : null;
            })}
                </>)}
        </div>);
};
const cellStyles = {
    container: css({
        display: "flex",
        alignItems: "center",
        width: "100%",
        height: "4rem",
    }),
    changedValueIndicator: css({
        marginLeft: "auto",
    }),
    readonlyContainer: css({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: space[8],
        width: "100%",
        height: "100%",
        padding: `${space[8]} 0`,
    }),
};
